import React, { useEffect, useMemo, useState } from "react";
import mermaid from "mermaid";
import Modal from "./Modal.jsx";

mermaid.initialize({
  startOnLoad: false,
  securityLevel: "loose",
  theme: "default",
});

function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function downloadText(filename, text, mime = "text/plain") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  URL.revokeObjectURL(url);
}

function downloadSvg(filename, svgText) {
  const blob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  URL.revokeObjectURL(url);
}

function getNumericSize(attrVal) {
  if (!attrVal) return null;
  const s = String(attrVal).trim();
  if (s.endsWith("%")) return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function normalizeSvgForExport(svgText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, "image/svg+xml");
  const svgEl = doc.querySelector("svg");
  if (!svgEl) return { svg: svgText, width: 1200, height: 800 };

  if (!svgEl.getAttribute("xmlns")) {
    svgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  }

  let w = getNumericSize(svgEl.getAttribute("width"));
  let h = getNumericSize(svgEl.getAttribute("height"));

  const vb = svgEl.getAttribute("viewBox");
  if ((!w || !h) && vb) {
    const parts = vb.split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((x) => Number.isFinite(x))) {
      w = w || parts[2];
      h = h || parts[3];
    }
  }

  w = w || 1200;
  h = h || 800;

  svgEl.setAttribute("width", String(w));
  svgEl.setAttribute("height", String(h));

  const serializer = new XMLSerializer();
  return { svg: serializer.serializeToString(svgEl), width: w, height: h };
}

async function downloadPngFromSvg(svgText, filename, scale = 2) {
  const { svg, width, height } = normalizeSvgForExport(svgText);

  const encoded = encodeURIComponent(svg).replace(/'/g, "%27").replace(/"/g, "%22");
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encoded}`;

  const img = new Image();
  img.decoding = "async";

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = dataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("PNG export failed (canvas.toBlob returned null)");

  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  URL.revokeObjectURL(url);
}

export default function MermaidViewer({
  code,
  title = "Architecture Diagram",
  fileBase = "architecture",
}) {
  const [svg, setSvg] = useState("");
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  // ✅ zoom state
  const [zoom, setZoom] = useState(1);

  // Sanitize Mermaid SOURCE code to prevent XSS via injected node labels.
  // We strip dangerous HTML (script/iframe/object/embed tags, event handlers,
  // javascript: URIs) from the source *before* Mermaid renders it.
  // This is safer than sanitising the SVG output, which breaks Mermaid's
  // <foreignObject>-based text rendering.
  const safeCode = useMemo(() => {
    let raw = (code || "").trim();
    if (!raw) return raw;
    // Remove dangerous tags that could execute scripts
    raw = raw.replace(/<\s*\/?\s*(script|iframe|object|embed|applet|form)\b[^>]*>/gi, "");
    // Remove event-handler attributes (onclick, onerror, onload, etc.)
    raw = raw.replace(/\bon[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    // Remove javascript: / data: URIs
    raw = raw.replace(/javascript\s*:/gi, "");
    raw = raw.replace(/data\s*:\s*text\/html/gi, "");
    return raw;
  }, [code]);

  const render = async () => {
    setErr("");
    setSvg("");
    if (!safeCode) return;

    try {
      const id = `mmd-${Math.random().toString(36).slice(2)}`;
      const out = await mermaid.render(id, safeCode);
      setSvg(out.svg || "");
    } catch (e) {
      setErr(String(e?.message || e));
    }
  };

  useEffect(() => {
    render();
    // reset zoom when diagram changes
    setZoom(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeCode]);

  const clamp = (v) => Math.max(0.4, Math.min(3, v));

  const zoomIn = () => setZoom((z) => clamp(Number((z + 0.2).toFixed(2))));
  const zoomOut = () => setZoom((z) => clamp(Number((z - 0.2).toFixed(2))));
  const zoomReset = () => setZoom(1);

  // "Fit" in this layout means reset (since SVG is width:100%), but we keep it as a UX action.
  const zoomFit = () => setZoom(1);

  const onDownloadSvg = () => {
    if (!svg) return;
    downloadSvg(`${fileBase}.svg`, svg);
  };

  const onDownloadPng = async () => {
    if (!svg) return;
    try {
      setBusy(true);
      await downloadPngFromSvg(svg, `${fileBase}.png`, 2);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const onDownloadMmd = () => {
    if (!safeCode) return;
    downloadText(`${fileBase}.mmd`, safeCode, "text/plain");
  };

  const ZoomControls = (
    <div className="mmd-zoom">
      <button className="btn" onClick={zoomOut} disabled={!svg}>
        −
      </button>
      <div className="mmd-zoom-label">{Math.round(zoom * 100)}%</div>
      <button className="btn" onClick={zoomIn} disabled={!svg}>
        +
      </button>
      <button className="btn" onClick={zoomReset} disabled={!svg}>
        Reset
      </button>
      <button className="btn" onClick={zoomFit} disabled={!svg}>
        Fit
      </button>
    </div>
  );

  const DiagramBody = (
    <div className="mmd-wrap">
      <div className="mmd-toolbar">
        <div className="mmd-title">{title}</div>

        <div className="mmd-actions">
          {ZoomControls}

          <button className="btn" onClick={onDownloadMmd} disabled={!safeCode}>
            Download .mmd
          </button>
          <button className="btn" onClick={onDownloadSvg} disabled={!svg}>
            Download SVG
          </button>
          <button className="btn" onClick={onDownloadPng} disabled={!svg || busy}>
            {busy ? "Preparing…" : "Download PNG"}
          </button>
          <button className="btn primary" onClick={() => setExpanded(true)} disabled={!svg}>
            Expand
          </button>
        </div>
      </div>

      {err ? (
        <div className="error">Mermaid error: {err}</div>
      ) : !svg ? (
        <div className="muted">No diagram yet.</div>
      ) : (
        <div className="mmd-canvas">
          <div
            className="mmd-zoom-surface"
            style={{ transform: `scale(${zoom})`, transformOrigin: "0 0" }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      {DiagramBody}

      <Modal
        open={expanded}
        title="Architecture Diagram (Expanded)"
        onClose={() => setExpanded(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            {ZoomControls}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn" onClick={onDownloadMmd} disabled={!safeCode}>
                Download .mmd
              </button>
              <button className="btn" onClick={onDownloadSvg} disabled={!svg}>
                Download SVG
              </button>
              <button className="btn" onClick={onDownloadPng} disabled={!svg || busy}>
                {busy ? "Preparing…" : "Download PNG"}
              </button>
              <button className="btn primary" onClick={() => setExpanded(false)}>
                Close
              </button>
            </div>
          </div>
        }
      >
        {err ? (
          <div className="error">Mermaid error: {err}</div>
        ) : !svg ? (
          <div className="muted">No diagram yet.</div>
        ) : (
          <div className="mmd-expanded">
            <div
              className="mmd-zoom-surface"
              style={{ transform: `scale(${zoom})`, transformOrigin: "0 0" }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          </div>
        )}
      </Modal>
    </>
  );
}
